'use client';

import { useState } from 'react';
import { Navigation, Home } from 'lucide-react';
import siteTheme from '@/lib/theme';
const { colors, fonts } = siteTheme;

interface Props {
  mapEmbedUrl: string;
  destinationName: string;
  destinationAddress?: string;
}

const IconOrigin = () => <Navigation size={18} color={colors.gold} style={{ flexShrink: 0 }} />;
const IconDestination = () => <Home size={18} color={colors.navyCta} strokeWidth={1.5} style={{ flexShrink: 0, marginTop: '0.15rem' }} />;

export default function MapWithDirections({ mapEmbedUrl, destinationName, destinationAddress }: Props) {
  const [origin, setOrigin] = useState('');

  const handleGetDirection = () => {
    if (!origin.trim()) return;
    const dest = destinationAddress || destinationName;
    const url = `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(origin.trim())}&destination=${encodeURIComponent(dest)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: `
        .mwd-widget {
          position: absolute;
          top: 50%;
          left: 2.8rem;
          transform: translateY(-50%);
          z-index: 10;
          background: #fff;
          border-radius: 1rem;
          padding: 2.8rem 2.4rem;
          box-shadow: 0 8px 40px rgba(0,0,0,0.18);
          width: clamp(28rem, 28vw, 36rem);
        }
        .mwd-input {
          width: 100%;
          border: none;
          outline: none;
          font-family: var(--font-sans);
          font-size: 1.4rem;
          font-weight: 300;
          color: var(--navy-deep);
          background: transparent;
        }
        .mwd-input::placeholder { color: rgba(42,49,130,0.35); }
        .mwd-btn {
          width: 100%;
          padding: 1.8rem 1.4rem;
          background: var(--navy-cta);
          color: #fff;
          border: none;
          border-radius: 0.6rem;
          font-family: var(--font-sans);
          font-size: 1.5rem;
          font-weight: 500;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          cursor: pointer;
          position: relative;
          overflow: hidden;
          transition: background 0.25s, box-shadow 0.25s, opacity 0.2s;
        }
        .mwd-btn::before {
          content: '';
          position: absolute;
          top: -10%; left: -120%;
          width: 55%; height: 120%;
          background: linear-gradient(105deg, transparent 20%, rgba(255,255,255,0.22) 50%, transparent 80%);
          transform: skewX(-15deg);
          pointer-events: none;
        }
        .mwd-btn:not(:disabled):hover {
          background: var(--navy-mid);
          box-shadow: 0 0 22px rgba(0,58,93,0.55), 0 0 48px rgba(0,58,93,0.22), inset 0 0 0 1px rgba(100,150,180,0.35);
        }
        .mwd-btn:not(:disabled):hover::before { animation: luxury-shine 0.6s ease forwards; }
        .mwd-btn:disabled { opacity: 0.45; cursor: not-allowed; }
        @media (max-width: 640px) {
          .mwd-widget {
            position: static;
            width: 100%;
            border-radius: 0;
            box-shadow: none;
            border-top: 1px solid var(--gold-pale);
            padding: 2.4rem clamp(2rem, 5vw, 4rem);
          }
        }
      ` }} />

      <div style={{ position: 'relative', lineHeight: 0 }}>
        {/* Map iframe */}
        <iframe
          src={mapEmbedUrl}
          width="100%"
          height="540"
          style={{ border: 0, display: 'block' }}
          loading="lazy"
          allowFullScreen
          referrerPolicy="no-referrer-when-downgrade"
          title="Resort location map"
        />

        {/* Directions widget */}
        <div className="mwd-widget">
          <h3 style={{
            fontFamily: fonts.serif,
            fontWeight: 300,
            fontSize: 'clamp(1.8rem, 2vw, 2.2rem)',
            letterSpacing: '0.03em',
            color: colors.navyDeep,
            marginBottom: '2rem',
          }}>
            How to Get Here
          </h3>

          {/* Origin row */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: '1rem',
            border: `1px solid ${colors.goldPale}`,
            borderRadius: '0.5rem',
            padding: '1rem 1.4rem',
          }}>
            <IconOrigin />
            <input
              className="mwd-input"
              type="text"
              value={origin}
              onChange={e => setOrigin(e.target.value)}
              placeholder="Enter your city or location"
              onKeyDown={e => e.key === 'Enter' && handleGetDirection()}
            />
          </div>

          {/* Dotted connector */}
          <div style={{
            marginLeft: '2rem',
            borderLeft: `2px dashed ${colors.goldPale}`,
            height: '1.8rem',
          }} />

          {/* Destination row (fixed) */}
          <div style={{
            display: 'flex', alignItems: 'flex-start', gap: '1rem',
            border: `1px solid ${colors.goldPale}`,
            borderRadius: '0.5rem',
            padding: '1rem 1.4rem',
            marginBottom: '2rem',
            backgroundColor: colors.bgAlt,
          }}>
            <IconDestination />
            <span style={{
              fontFamily: fonts.sans,
              fontSize: '1.4rem',
              fontWeight: 400,
              color: colors.navyDeep,
              lineHeight: 1.5,
            }}>
              {destinationName}
            </span>
          </div>

          {/* Button */}
          <button
            className="mwd-btn"
            onClick={handleGetDirection}
            disabled={!origin.trim()}
          >
            Get Direction
          </button>
        </div>
      </div>
    </>
  );
}
